import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { CustomerProfile, MarketingChannel, MarketingContext, LoyaltyProfile } from '@/models';
import { getSignInPath } from '@/router';
import { formatPhoneNumber } from '@/helpers';
import moment from 'moment';
import { CustomerError } from '@/services/customer/customer.service';
import { EnrollmentOptions } from '@/services/loyalty/loyalty.service';
import { CountryCode } from '@/store/ui';
import { AccountManagementEvent, AccountPageLoadEvent } from '@/services/analytics/events';
import { InteractionTrackingType } from '@/services/analytics/platforms/adobe-analytics/models';


@Component({
  name: 'CmgPersonalAndPreferencesPage',
})
export default class CmgPersonalAndPreferencesPage extends Vue {
  subscribeToEmail = false;
  subscribeToText = false;
  isJoiningRewards = false;
  isSubscribingEmail = false;
  isSubscribingText = false;
  hasSubscriptionError = false;
  errorMessage = '';
  loyaltySubscriptionStartDate = '';
  loyaltyMembershipId = '';

  async mounted() {
    this.$services.customer.refreshCustomerProfileInfo();
    if (!(await this.$services.customer.waitForCustomerInitialization())) {
      this.$router.push(getSignInPath(this.$route.path));
    }
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get isCanadian() {
    return this.customer && this.customer.country == CountryCode.CA;
  }

  get loyaltyProfile(): LoyaltyProfile | null {
    if (!this.customer) {
      return null;
    }

    return this.$services.loyalty.getLoyaltyProfile(this.customer);
  }

  get isEnrolledInLoyalty(): boolean {
    if (!this.customer) {
      return false;
    }

    const profile = this.$services.loyalty.getLoyaltyProfile(this.customer);
    return profile && profile.memberId ? true : false;
  }

  get fullCustomerName(): string {
    if (!this.customer) {
      return '';
    }

    return `${this.customer.firstName || ''} ${this.customer.lastName}`;
  }

  get phoneNumber(): string {
    if (!this.customer) {
      return '';
    }

    return formatPhoneNumber(this.customer.phoneNumber);
  }

  get email(): string {
    if (!this.customer) {
      return '';
    }

    return this.customer.email;
  }

  get birthday(): string {
    if (!this.customer || !this.customer.dateOfBirth) {
      return '';
    }

    return moment(this.customer.dateOfBirth).format('MMMM DD');
  }

  get emailUpdatesText(): string {
    if (!this.$appStore.state.cms.joinRewards) {
      return '';
    }

    return this.$appStore.state.cms.joinRewards.emailUpdates;
  }

  get legalCopy(): string {
    return this.$appStore.state.cms.legalCopy?.personalPreference ?? '';
  }

  get country(): string {
    if (this.customer && this.customer.country) {
      return this.isCanadian ? 'Canada' : 'United States';
    }
    return '';
  }

  get dietsAndAllergensSymbol(): string {
    return this.$appStore.state.cms.images.icons.nutritionFilter.default;
  }

  get selectTagsText(): string {
    return this.hasDietaryTagsSet ? 'Change' : 'Select Tags';
  }

  get hasDietaryTagsSet(): boolean {
    return this.customer?.dietaryTags != null && this.customer.dietaryTags.length > 0;
  }

  setloyaltyMembershipId() {
    this.loyaltyMembershipId = (this.loyaltyProfile && this.loyaltyProfile.memberId) || '';
  }

  setloyaltySubscriptionStartDate() {
    if (!this.loyaltyProfile) {
      return;
    }

    this.loyaltySubscriptionStartDate = moment(this.loyaltyProfile.subscribeDate).format('YY');
  }

  @Watch('customer', { immediate: true, deep: true })
  onCustomerUpdated(customer: CustomerProfile | null) {
    this.refreshIsSubscribedToEmail();
    this.refreshIsSubscribedToText();
    this.errorMessage = '';
    this.setloyaltyMembershipId();
    this.setloyaltySubscriptionStartDate();
  }

  async onClickTextSubscription(): Promise<void> {
    this.isSubscribingText = true;
    this.hasSubscriptionError = false;

    try {
      if (this.subscribeToText) {
        await this.$services.customer.subscribeToMarketingChannel(MarketingChannel.Sms, MarketingContext.Marketing);
      } else {
        await this.$services.customer.unsubscribeToMarketingChannel(MarketingChannel.Sms, MarketingContext.Marketing);
      }

      this.$services.analytics.send(new AccountManagementEvent(this.subscribeToText ? InteractionTrackingType.ToggleTextOn : InteractionTrackingType.ToggleTextOff));

      this.$services.customer.refreshCustomerProfileInfo();
    } catch (err) {
      this.hasSubscriptionError = true;
      this.subscribeToText = !this.subscribeToText;

      if (err instanceof CustomerError) {
        this.errorMessage = err.message;
      } else {
        throw err;
      }
    }

    this.isSubscribingText = false;
  }

  async onClickEmailSubscription(): Promise<void> {
    this.isSubscribingEmail = true;
    this.hasSubscriptionError = false;

    try {
      if (this.subscribeToEmail) {
        await this.$services.customer.subscribeToMarketingChannel(MarketingChannel.Email, MarketingContext.Marketing);
      } else {
        await this.$services.customer.unsubscribeToMarketingChannel(MarketingChannel.Email, MarketingContext.Marketing);
      }

      this.$services.analytics.send(new AccountManagementEvent(this.subscribeToEmail ? InteractionTrackingType.ToggleEmailOn : InteractionTrackingType.ToggleEmailOff));

      this.$services.customer.refreshCustomerProfileInfo();
    } catch (err) {
      this.hasSubscriptionError = true;
      this.subscribeToEmail = !this.subscribeToEmail;

      if (err instanceof CustomerError) {
        this.errorMessage = err.message;
      } else {
        throw err;
      }
    }

    this.isSubscribingEmail = false;
  }

  onEditInfo(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('editAccountModal', true);
    this.$services.analytics.send(new AccountPageLoadEvent('update-info'));
    this.$services.ui.firstFocus();
  }

  onUpdatePassword(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('updatePasswordModal', true);
    this.$services.analytics.send(new AccountPageLoadEvent('update-password'));
    this.$services.ui.firstFocus();
  }

  onLeaveRewards(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('leaveRewardsModal', true);
    this.$services.analytics.send(new AccountPageLoadEvent('leave-rewards'));
    this.$services.ui.firstFocus();
    this.$services.customer.setJoinedLoyaltyOnOrderConfirmationPage(false);
  }

  onSelectTags(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('dietaryFiltersModal', true);
    this.$services.ui.firstFocus();
  }

  onClickLearnMore(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('twoStepVerificationLearnMoreModal', true);
    this.$services.analytics.send(new AccountPageLoadEvent('two-step-verification-learn-more'));
    this.$services.ui.firstFocus();
  }

  async onClickJoinRewards() {
    this.isJoiningRewards = true;
    try {
      const enrollmentOptions: EnrollmentOptions = { 
        customer: this.customer, 
        pageTitle: document.title, 
        shouldSubscribeToMarketingEmail: this.customer?.country === CountryCode.US,
        shouldSubscribeToMarketingSMS: this.subscribeToText, 
      };
      
      await this.$services.loyalty.enrollInLoyalty(enrollmentOptions);
      this.$services.ui.showOnboardingModal(false, this.$services.customer, this.customer ?? undefined);

      await this.$services.customer.refreshCustomerProfileInfo();
      
      this.$services.adobeTarget.sendAdobeTargetExperienceRequest(this.$route.name);
    } catch (e) {
      // TODO-25q6xt: handle errors
      this.$services.logger.error('Something went wrong while enrolling in loyalty', { error: e });
    } finally {
      this.isJoiningRewards = false;
    }

    this.$services.analytics.send(new AccountManagementEvent('personal-preferences|join-chipotle-rewards'));
    this.$services.ui.firstFocus();
  }

  private refreshIsSubscribedToEmail(): void {
    this.subscribeToEmail = this.$services.customer.hasSubscription(MarketingChannel.Email, MarketingContext.Marketing);
  }

  private refreshIsSubscribedToText(): void {
    this.subscribeToText = this.$services.customer.hasSubscription(MarketingChannel.Sms, MarketingContext.Marketing);
  }
}
